import React, { useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, Grid, LinearProgress, Divider, Chip, Stack, useTheme } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PercentIcon from '@mui/icons-material/Percent';
import CategoryIcon from '@mui/icons-material/Category';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface ClassificationSummaryProps {
  data: any[];
}

const ClassificationSummary: React.FC<ClassificationSummaryProps> = ({ data }) => {
  const theme = useTheme();
  const [summary, setSummary] = useState({
    totalItems: 0,
    classifiedItems: 0,
    averageConfidence: 0,
    confidenceBrackets: {
      high: 0, // 90-100%
      medium: 0, // 70-89%
      low: 0, // 0-69%
    }
  });

  useEffect(() => {
    if (!data || data.length === 0) {
      console.log('No data provided to ClassificationSummary');
      return;
    }

    console.log('Processing classification data for summary');

    // Count totals
    const totalItems = data.length;
    let classifiedItems = 0;
    let confidenceSum = 0;
    
    // Confidence brackets
    const confidenceBrackets = {
      high: 0, // 90-100%
      medium: 0, // 70-89%
      low: 0, // 0-69%
    };

    data.forEach(item => {
      // Count classified items (those with a category and confidence)
      if (item.category && item.confidence !== undefined) {
        classifiedItems++;
        
        // Sum confidence for average calculation
        const confidence = typeof item.confidence === 'number' ? 
          item.confidence : parseFloat(item.confidence);
        
        if (!isNaN(confidence)) {
          confidenceSum += confidence;
          
          // Categorise by confidence bracket
          if (confidence >= 90) {
            confidenceBrackets.high++;
          } else if (confidence >= 70) {
            confidenceBrackets.medium++;
          } else {
            confidenceBrackets.low++;
          }
        }
      }
    });

    // Calculate average confidence
    const averageConfidence = classifiedItems > 0 ? 
      Math.round(confidenceSum / classifiedItems) : 0;

    console.log('Classification summary calculated:', {
      totalItems,
      classifiedItems,
      averageConfidence,
      confidenceBrackets
    });

    setSummary({
      totalItems,
      classifiedItems,
      averageConfidence,
      confidenceBrackets,
    });
  }, [data]);

  const getConfidenceColor = (value: number) => {
    if (value >= 90) return theme.palette.success.main;
    if (value >= 70) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Classification Summary */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack spacing={2}>
                <Box display="flex" alignItems="center">
                  <AutoAwesomeIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Classification Overview</Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Items Classified
                  </Typography>
                  <Box display="flex" alignItems="flex-end">
                    <Typography variant="h4" fontWeight="bold">
                      {summary.classifiedItems}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1, mb: 0.5 }}>
                      / {summary.totalItems} items
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={summary.totalItems ? (summary.classifiedItems / summary.totalItems) * 100 : 0} 
                    sx={{ mt: 1, height: 8, borderRadius: 1 }}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Confidence Score */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack spacing={2}>
                <Box display="flex" alignItems="center">
                  <PercentIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Confidence Score</Typography>
                </Box>
                
                <Box display="flex" flexDirection="column" alignItems="center">
                  <Box
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: 120,
                      height: 120,
                      borderRadius: '50%',
                      bgcolor: 'background.paper',
                      boxShadow: 1,
                      mb: 2,
                    }}
                  >
                    <Typography 
                      variant="h3" 
                      align="center"
                      fontWeight="bold"
                      sx={{ color: getConfidenceColor(summary.averageConfidence) }}
                    >
                      {summary.averageConfidence}%
                    </Typography>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        borderRadius: '50%',
                        border: `6px solid ${getConfidenceColor(summary.averageConfidence)}`,
                        opacity: 0.3,
                      }}
                    />
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" align="center">
                    Average Confidence Score
                  </Typography>
                </Box>
                
                <Box>
                  <Stack direction="row" spacing={1} justifyContent="center">
                    <Chip 
                      icon={<CheckCircleIcon />} 
                      label={`${summary.confidenceBrackets.high} High`}
                      sx={{ bgcolor: theme.palette.success.light, color: theme.palette.success.contrastText }}
                    />
                    <Chip 
                      label={`${summary.confidenceBrackets.medium} Medium`}
                      sx={{ bgcolor: theme.palette.warning.light, color: theme.palette.warning.contrastText }}
                    />
                    <Chip 
                      label={`${summary.confidenceBrackets.low} Low`}
                      sx={{ bgcolor: theme.palette.error.light, color: theme.palette.error.contrastText }}
                    />
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ClassificationSummary; 